import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Organizations from "@/pages/Organizations";
import Dependencies from "@/pages/Dependencies";
import FailureDomains from "@/pages/FailureDomains";
import Vulnerabilities from "@/pages/Vulnerabilities";
import VulnerabilityDetail from "@/pages/VulnerabilityDetail";
import Threats from "@/pages/Threats";
import ThreatDetail from "@/pages/ThreatDetail";
import Mitre from "@/pages/Mitre";
import Controls from "@/pages/Controls";
import ControlDetail from "@/pages/ControlDetail";
import Risks from "@/pages/Risks";
import Scenarios from "@/pages/Scenarios";
import ScenarioDetail from "@/pages/ScenarioDetail";
import SimulationResult from "@/pages/SimulationResult";
import AuditLog from "@/pages/AuditLog";
import Users from "@/pages/Users";
import TenantAdmin from "@/pages/TenantAdmin";
import DigitalTwin from "@/pages/DigitalTwin";
import SupplyChain from "@/pages/SupplyChain";
import Compliance from "@/pages/Compliance";
import FirstPassword from "@/pages/FirstPassword";
import Activate from "@/pages/Activate";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  // Parcours OBLIGATOIRE : un compte must_reset ne peut aller nulle part sauf changer son mot de passe.
  if (user.must_reset && location.pathname !== "/first-password")
    return <Navigate to="/first-password" replace />;
  return children;
}

// Accueil : le Global Admin (pas de dashboard métier, mais tenant:read) est routé vers la gestion des tenants.
function HomeRoute() {
  const { user, can } = useAuth();
  if (user && user !== false && !can("dashboard:read") && can("tenant:read"))
    return <Navigate to="/tenants" replace />;
  return <Dashboard />;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user && user !== false) return <Navigate to="/" replace />;
  return children;
}

// Garde de permission au niveau route (défense en profondeur ; le backend reste la source de vérité).
function RequirePerm({ perm, children }) {
  const { can } = useAuth();
  if (!can(perm)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/first-password" element={<Protected><FirstPassword /></Protected>} />
            <Route path="/" element={<Protected><HomeRoute /></Protected>} />
            <Route path="/tenants" element={<Protected><RequirePerm perm="tenant:read"><TenantAdmin /></RequirePerm></Protected>} />
            <Route path="/assets" element={<Protected><Assets /></Protected>} />
            <Route path="/assets/:id" element={<Protected><AssetDetail /></Protected>} />
            <Route path="/services" element={<Protected><Services /></Protected>} />
            <Route path="/services/:id" element={<Protected><ServiceDetail /></Protected>} />
            <Route path="/dependencies" element={<Protected><Dependencies /></Protected>} />
            <Route path="/failure-domains" element={<Protected><FailureDomains /></Protected>} />
            <Route path="/failure-domains/:id" element={<Protected><FailureDomains /></Protected>} />
            <Route path="/vulnerabilities" element={<Protected><Vulnerabilities /></Protected>} />
            <Route path="/vulnerabilities/:id" element={<Protected><VulnerabilityDetail /></Protected>} />
            <Route path="/threats" element={<Protected><Threats /></Protected>} />
            <Route path="/threats/:id" element={<Protected><ThreatDetail /></Protected>} />
            <Route path="/mitre" element={<Protected><Mitre /></Protected>} />
            <Route path="/controls" element={<Protected><Controls /></Protected>} />
            <Route path="/controls/:id" element={<Protected><ControlDetail /></Protected>} />
            <Route path="/risks" element={<Protected><Risks /></Protected>} />
            <Route path="/scenarios" element={<Protected><Scenarios /></Protected>} />
            <Route path="/scenarios/:id" element={<Protected><ScenarioDetail /></Protected>} />
            <Route path="/twin" element={<Protected><RequirePerm perm="twin:read"><DigitalTwin /></RequirePerm></Protected>} />
            <Route path="/supply-chain" element={<Protected><RequirePerm perm="supply_chain:read"><SupplyChain /></RequirePerm></Protected>} />
            <Route path="/compliance" element={<Protected><RequirePerm perm="control:read"><Compliance /></RequirePerm></Protected>} />
            <Route path="/simulations/:runId" element={<Protected><SimulationResult /></Protected>} />
            <Route path="/organizations" element={<Protected><Organizations /></Protected>} />
            <Route path="/audit" element={<Protected><AuditLog /></Protected>} />
            <Route path="/users" element={<Protected><Users /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
